import { supabase } from './supabase'
import {
  startTransactionTracking,
  updateTransactionOperation,
  completeTransaction,
  failTransaction,
  markTransactionRolledBack,
} from './transactionState'
import { validateTransaction } from '../utils/transactionValidation'

/**
 * Execute multiple database operations in a transaction
 * Note: Supabase doesn't support true transactions, so we use a rollback pattern
 */
export const executeTransaction = async (operations, options = {}) => {
  const {
    onRollback,
    onSuccess,
    organizationId,
    userId,
    transactionType = 'generic',
    skipValidation = false,
  } = options

  // Generate transaction ID
  const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Validate transaction before execution
  if (!skipValidation) {
    const validation = await validateTransaction(transactionType, null, operations, organizationId)
    if (!validation.valid) {
      const error = new Error('Transaction validation failed')
      error.validationErrors = validation.errors
      throw error
    }
  }

  // Start tracking transaction
  let transactionState = null
  if (organizationId && userId) {
    transactionState = await startTransactionTracking(
      transactionId,
      organizationId,
      userId,
      transactionType,
      operations
    )
  }

  const rollbackOperations = []
  const results = []

  try {
    // Execute operations sequentially
    for (let i = 0; i < operations.length; i++) {
      const operation = operations[i]
      const { table, action, data, filters, rollbackData } = operation

      // Update operation status to in_progress
      if (transactionState) {
        await updateTransactionOperation(transactionId, i, 'in_progress')
      }

      let result

      switch (action) {
        case 'insert':
          const { data: insertData, error: insertError } = await supabase
            .from(table)
            .insert(data)
            .select()
            .single()

          if (insertError) {
            // Update operation status to failed
            if (transactionState) {
              await updateTransactionOperation(transactionId, i, 'failed', null, insertError)
            }
            throw insertError
          }

          result = insertData
          // Store rollback info (delete the inserted record)
          rollbackOperations.push({
            table,
            action: 'delete',
            filters: { id: insertData.id },
            originalData: insertData,
          })

          // Update operation status to completed
          if (transactionState) {
            await updateTransactionOperation(transactionId, i, 'completed', result)
          }
          break

        case 'update':
          // For updates, we should get the updated row(s) but handle multiple rows
          let updateQuery = supabase
            .from(table)
            .update(data)
            .match(filters || {})
            .select()
          
          // If filters include 'id', we can safely use .single() since id is unique
          // Otherwise, we need to handle multiple rows
          if (filters?.id) {
            updateQuery = updateQuery.single()
          } else {
            // If no id filter, limit to 1 and take first result
            updateQuery = updateQuery.limit(1)
          }
          
          const { data: updateData, error: updateError } = await updateQuery

          if (updateError) {
            // Update operation status to failed
            if (transactionState) {
              await updateTransactionOperation(transactionId, i, 'failed', null, updateError)
            }
            throw updateError
          }
          
          // Handle array result if we didn't use .single()
          const updateResult = Array.isArray(updateData) ? updateData[0] : updateData

          if (!updateResult) {
            throw new Error(`Update operation returned no data for table ${table} with filters: ${JSON.stringify(filters)}`)
          }

          result = updateResult
          // Store rollback info (restore original data)
          if (rollbackData) {
            rollbackOperations.push({
              table,
              action: 'update',
              filters: filters || { id: updateResult.id },
              data: rollbackData,
            })
          }

          // Update operation status to completed
          if (transactionState) {
            await updateTransactionOperation(transactionId, i, 'completed', result)
          }
          break

        case 'upsert':
          const { data: upsertData, error: upsertError } = await supabase
            .from(table)
            .upsert(data, { onConflict: options.conflictColumn || 'id' })
            .select()
            .single()

          if (upsertError) {
            // Update operation status to failed
            if (transactionState) {
              await updateTransactionOperation(transactionId, i, 'failed', null, upsertError)
            }
            throw upsertError
          }

          result = upsertData
          // Rollback depends on whether it was insert or update
          if (rollbackData) {
            rollbackOperations.push({
              table,
              action: 'update',
              filters: { id: upsertData.id },
              data: rollbackData,
            })
          } else {
            rollbackOperations.push({
              table,
              action: 'delete',
              filters: { id: upsertData.id },
            })
          }

          // Update operation status to completed
          if (transactionState) {
            await updateTransactionOperation(transactionId, i, 'completed', result)
          }
          break

        case 'delete':
          // For delete, we need to fetch the data first for rollback
          let deleteQuery = supabase
            .from(table)
            .select('*')
            .match(filters || {})
          
          // If filters include 'id', we can safely use .single() since id is unique
          if (filters?.id) {
            deleteQuery = deleteQuery.single()
          } else {
            // If no id filter, limit to 1 and take first result
            deleteQuery = deleteQuery.limit(1)
          }
          
          const { data: deleteData, error: deleteError } = await deleteQuery

          if (deleteError) throw deleteError
          
          // Handle case where no row was found
          if (!deleteData) {
            throw new Error(`No row found to delete in table ${table} with filters: ${JSON.stringify(filters)}`)
          }
          
          // Handle array result if we didn't use .single()
          const deleteResult = Array.isArray(deleteData) ? deleteData[0] : deleteData

          const { error: deleteExecError } = await supabase
            .from(table)
            .delete()
            .match(filters || {})

          if (deleteExecError) {
            // Update operation status to failed
            if (transactionState) {
              await updateTransactionOperation(transactionId, i, 'failed', null, deleteExecError)
            }
            throw deleteExecError
          }

          result = deleteResult
          // Store rollback info (re-insert the deleted record)
          rollbackOperations.push({
            table,
            action: 'insert',
            data: deleteResult,
          })

          // Update operation status to completed
          if (transactionState) {
            await updateTransactionOperation(transactionId, i, 'completed', result)
          }
          break

        default:
          throw new Error(`Unknown operation: ${action}`)
      }

      results.push(result)
    }

    // All operations succeeded
    if (onSuccess) {
      await onSuccess(results)
    }

    // Mark transaction as completed
    if (transactionState) {
      await completeTransaction(transactionId, results)
    }

    return { success: true, results, transactionId }
  } catch (error) {
    // Mark transaction as failed
    if (transactionState) {
      await failTransaction(transactionId, error, {
        operations: rollbackOperations,
        failedAt: new Date().toISOString(),
      })
    }

    // Rollback all operations in reverse order
    console.error('Transaction failed, rolling back:', error)

    const rollbackResults = {
      success: true,
      operationsRolledBack: 0,
      operationsFailed: 0,
    }

    try {
      for (let i = rollbackOperations.length - 1; i >= 0; i--) {
        const rollback = rollbackOperations[i]

        try {
          switch (rollback.action) {
            case 'delete':
              await supabase.from(rollback.table).delete().match(rollback.filters)
              rollbackResults.operationsRolledBack++
              break
            case 'update':
              await supabase
                .from(rollback.table)
                .update(rollback.data)
                .match(rollback.filters)
              rollbackResults.operationsRolledBack++
              break
            case 'insert':
              await supabase.from(rollback.table).insert(rollback.data)
              rollbackResults.operationsRolledBack++
              break
          }
        } catch (rollbackOpError) {
          console.error(`Failed to rollback operation ${i}:`, rollbackOpError)
          rollbackResults.operationsFailed++
        }
      }

      // Mark transaction as rolled back
      if (transactionState) {
        await markTransactionRolledBack(transactionId, {
          ...rollbackResults,
          rolledBackAt: new Date().toISOString(),
        })
      }

      if (onRollback) {
        await onRollback(error, rollbackOperations, rollbackResults)
      }

      // If rollback had failures, throw enhanced error
      if (rollbackResults.operationsFailed > 0) {
        throw new Error(
          `Transaction failed. Rollback partially failed: ${rollbackResults.operationsFailed} operations could not be rolled back. Transaction ID: ${transactionId}`
        )
      }
    } catch (rollbackError) {
      console.error('Rollback failed:', rollbackError)
      // Critical: Rollback failed - manual intervention may be needed
      const criticalError = new Error(
        `Transaction failed and rollback failed: ${rollbackError.message}. Original error: ${error.message}. Transaction ID: ${transactionId}`
      )
      criticalError.transactionId = transactionId
      criticalError.rollbackOperations = rollbackOperations
      throw criticalError
    }

    // Re-throw original error with transaction ID
    const enhancedError = new Error(error.message)
    enhancedError.transactionId = transactionId
    enhancedError.rollbackResults = rollbackResults
    throw enhancedError
  }
}

/**
 * Save examination with all related data atomically
 */
export const saveExaminationTransaction = async (
  organizationId,
  examinationData,
  userId = null
) => {
  const {
    client,
    medicalHistory,
    vitalSigns,
    customQuestions,
    examinationId,
  } = examinationData

  const operations = []

  // 1. Create or update client
  if (client.id) {
    // Get existing client data for rollback
    const { data: existingClient } = await supabase
      .from('clients')
      .select('*')
      .eq('id', client.id)
      .single()

    operations.push({
      table: 'clients',
      action: 'update',
      data: {
        ...client,
        organization_id: organizationId,
        updated_at: new Date().toISOString(),
      },
      filters: { id: client.id },
      rollbackData: existingClient,
    })
  } else {
    // Remove id field for new client insert (database will generate UUID)
    const { id, ...clientData } = client
    operations.push({
      table: 'clients',
      action: 'insert',
      data: {
        ...clientData,
        organization_id: organizationId,
      },
    })
  }

  // 2. Create or update medical history
  // Note: For new medical histories, we'll create it in onSuccess after client is created
  // because we need the client_id which isn't available yet
  if (medicalHistory.id) {
    const { data: existingHistory } = await supabase
      .from('medical_histories')
      .select('*')
      .eq('id', medicalHistory.id)
      .single()

    operations.push({
      table: 'medical_histories',
      action: 'update',
      data: {
        ...medicalHistory,
        updated_at: new Date().toISOString(),
      },
      filters: { id: medicalHistory.id },
      rollbackData: existingHistory,
    })
  }
  // For new medical histories, we'll handle it in onSuccess after client is created

  // 3. Create or update examination
  if (examinationId) {
    const { data: existingExam } = await supabase
      .from('examinations')
      .select('*')
      .eq('id', examinationId)
      .single()

    operations.push({
      table: 'examinations',
      action: 'update',
      data: {
        vital_signs: vitalSigns,
        custom_questions: customQuestions,
        updated_at: new Date().toISOString(),
      },
      filters: { id: examinationId },
      rollbackData: existingExam,
    })
  } else {
    // For new examinations, we'll insert after client and history are created
    // This will be handled in the onSuccess callback
  }

  // Validate examination data before transaction
  const { validateTransactionData } = await import('../utils/transactionValidation')
  const validation = validateTransactionData('examination', examinationData)
  if (!validation.valid) {
    throw new Error(`Validation failed: ${validation.errors.map((e) => e.message).join(', ')}`)
  }

  return executeTransaction(operations, {
    organizationId,
    userId,
    transactionType: 'examination',
    onSuccess: async (results) => {
      // Get client from results
      const clientResult = results.find((r) => r.first_name || r.last_name)
      
      // Create medical history if it's new (needs client_id from clientResult)
      let historyResult = results.find((r) => r.current_medications !== undefined || r.primary_goals)
      
      if (!medicalHistory.id && clientResult) {
        // Remove id field for new medical history insert
        const { id, ...historyData } = medicalHistory
        const { data: newHistory, error: historyError } = await supabase
          .from('medical_histories')
          .insert({
            ...historyData,
            client_id: clientResult.id,
            organization_id: organizationId,
          })
          .select()
          .single()
        
        if (historyError) throw historyError
        historyResult = newHistory
      }

      // Create examination if it's a new one
      if (!examinationId && clientResult && historyResult) {
        const { data: exam, error } = await supabase
          .from('examinations')
          .insert({
            organization_id: organizationId,
            client_id: clientResult.id,
            medical_history_id: historyResult.id,
            vital_signs: vitalSigns,
            custom_questions: customQuestions,
          })
          .select()
          .single()

        if (error) throw error
        return { success: true, results: [...results, exam] }
      }

      return { success: true, results }
    },
  })
}

/**
 * Save care plan with all related data atomically
 */
export const saveCarePlanTransaction = async (organizationId, carePlanData, userId = null) => {
  const { carePlan, therapies, nutritionTherapy } = carePlanData

  const operations = []

  // 1. Create or update care plan
  if (carePlan.id) {
    const { data: existingPlan } = await supabase
      .from('care_plans')
      .select('*')
      .eq('id', carePlan.id)
      .single()

    operations.push({
      table: 'care_plans',
      action: 'update',
      data: {
        ...carePlan,
        organization_id: organizationId,
        updated_at: new Date().toISOString(),
      },
      filters: { id: carePlan.id },
      rollbackData: existingPlan,
    })
  } else {
    operations.push({
      table: 'care_plans',
      action: 'insert',
      data: {
        ...carePlan,
        organization_id: organizationId,
      },
    })
  }

  // 2. Update therapies (if any)
  if (therapies && therapies.length > 0) {
    for (const therapy of therapies) {
      if (therapy.id) {
        const { data: existingTherapy } = await supabase
          .from('therapies')
          .select('*')
          .eq('id', therapy.id)
          .single()

        operations.push({
          table: 'therapies',
          action: 'update',
          data: therapy,
          filters: { id: therapy.id },
          rollbackData: existingTherapy,
        })
      } else {
        operations.push({
          table: 'therapies',
          action: 'insert',
          data: {
            ...therapy,
            organization_id: organizationId,
          },
        })
      }
    }
  }

  // Validate care plan data before transaction
  const { validateTransactionData } = await import('../utils/transactionValidation')
  const validation = validateTransactionData('carePlan', carePlanData)
  if (!validation.valid) {
    throw new Error(`Validation failed: ${validation.errors.map((e) => e.message).join(', ')}`)
  }

  return executeTransaction(operations, {
    organizationId,
    userId,
    transactionType: 'carePlan',
  })
}

